import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { Navigate, useParams } from 'react-router-dom';

import { ROUTES } from 'routes/route.constants';

import { getInvitationPreview } from 'services/invitationsService';

import AlreadyAccepted from './AlreadyAccepted';
import PendingInvite from './PendingInvite';
import UnableToAccept from './UnableToAccept';

import styles from './InviteFlow.module.scss';

const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  CANCELLED: 'cancelled',
};

const InviteFlow = () => {
  const { token } = useParams();
  const isLoggedIn = Boolean(localStorage.getItem('userObject'));

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);

      return;
    }

    // eslint-disable-next-line compat/compat -- IE11 not in browserslist
    const abortController = new AbortController();

    const fetchInvitation = async () => {
      try {
        const data = await getInvitationPreview(token);

        if (!abortController.signal.aborted) {
          setInvitation(data);
        }
      }
      catch (err) {
        if (err?.code === 'ERR_CANCELED') {
          return;
        }

        if (!abortController.signal.aborted) {
          setFetchFailed(true);
        }
      }
      finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchInvitation();

    return () => {
      abortController.abort();
    };
  }, [token]);

  if (loading) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor="#28BDAE" />
      </div>
    );
  }

  if (fetchFailed || !invitation || !token) {
    return <UnableToAccept />;
  }

  if (invitation.status === INVITATION_STATUS.CANCELLED) {
    return <UnableToAccept title="This invitation has been cancelled" />;
  }

  if (invitation.status === INVITATION_STATUS.PENDING && !isLoggedIn) {
    const returnTo = encodeURIComponent(ROUTES.TEAM_INVITATION(token));

    return <Navigate to={`${ROUTES.TEAM_LOGIN}?returnTo=${returnTo}`} replace />;
  }

  if (invitation.status === INVITATION_STATUS.PENDING) {
    return (
      <PendingInvite invitation={invitation} token={token} />
    );
  }

  if (invitation.status === INVITATION_STATUS.ACCEPTED) {
    return <AlreadyAccepted />;
  }

  return <UnableToAccept />;
};

export default InviteFlow;
